import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import { getServiceBySlug, icons, SERVICES } from '../config/services';

const ServiceDetailsPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const service = slug ? getServiceBySlug(slug) : undefined;
  
  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl font-bold mb-4 text-center">Service Not Found</h1>
        <p className="text-gray-600 mb-6 text-center">
          The service you're looking for doesn't exist. Please visit our services page to see all available options.
        </p>
        <button
          onClick={() => navigate('/services')}
          className="flex items-center text-primary hover:text-primary-dark transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Services
        </button>
      </div>
    );
  }

  const IconComponent = icons[service.icon as keyof typeof icons];
  
  // Get related services (excluding current)
  const relatedServices = SERVICES.filter(s => s.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={service.image_url}
          alt={service.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/60 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 pb-12">
            <Link
              to="/services"
              className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-4">
              {IconComponent && (
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
              )}
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-white">
                {service.title}
              </h1>
            </div>
            <p className="text-xl text-gray-200 max-w-2xl">
              {service.short_description}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Description & Features */}
          <div className="lg:col-span-2 space-y-12">
            {/* Description */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-secondary mb-4">
                About This Service
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {service.long_description}
              </p>
            </section>

            {/* Features */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-secondary mb-6">
                What's Included
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {service.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-neutral-cream rounded-lg"
                  >
                    <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Benefits */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-secondary mb-6">
                Why Choose This Service
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full" />
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-white border border-gray-200 rounded-xl shadow-lg p-6">
              <div className="text-center mb-6">
                <span className="text-sm text-gray-500 uppercase tracking-wide">
                  {service.pricing.type}
                </span>
                <p className="text-gray-600 mt-2">
                  {service.pricing.description}
                </p>
              </div>
              
              <Link
                to="/reservation"
                className="block w-full bg-primary text-white text-center py-4 rounded-lg font-semibold hover:bg-primary-dark transition-colors mb-4"
              >
                Book Now
              </Link>
              
              <Link
                to="/pricing"
                className="block w-full border border-secondary text-secondary text-center py-4 rounded-lg font-semibold hover:bg-secondary hover:text-white transition-colors"
              >
                View All Pricing
              </Link>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-secondary mb-3">Need Help?</h4>
                <p className="text-gray-600 text-sm mb-3">
                  Our team is available 24/7 to assist you with any questions.
                </p>
                <a
                  href="tel:+32123456789"
                  className="text-primary font-medium hover:underline"
                >
                  +32 123 456 789
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Related Services */}
        <section className="mt-20 pt-12 border-t border-gray-200">
          <h2 className="text-2xl font-serif font-bold text-secondary mb-8">
            Related Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedServices.map((relatedService) => {
              const RelatedIcon = icons[relatedService.icon as keyof typeof icons];
              return (
                <Link
                  key={relatedService.id}
                  to={`/services/${relatedService.slug}`}
                  className="group bg-neutral-cream rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-3">
                    {RelatedIcon && (
                      <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                        <RelatedIcon className="w-5 h-5 text-primary" />
                      </div>
                    )}
                    <h3 className="font-semibold text-secondary group-hover:text-primary transition-colors">
                      {relatedService.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {relatedService.short_description}
                  </p>
                  <div className="flex items-center text-primary text-sm font-medium">
                    Learn more <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServiceDetailsPage;
